import { useEffect, useMemo, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Link } from "react-router-dom"
import { AmpelBadge } from "@/components/AmpelBadge"
import {
  createPruefung,
  deletePruefung,
  duplizierePruefung,
  erstellePruefungServiceticket,
  fetchAnlagen,
  fetchPruefung,
  fetchPruefungen,
  fetchPruefungsarten,
  updatePruefung,
  type Pruefung,
  type PruefungEingabe,
} from "@/lib/api"
import { Button, Input, Select, Textarea } from "@/components/ui"
import { DataGrid, type ColumnDef } from "@/components/DataGrid"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SHEET_WIDTH,
} from "@/components/Sheet"
import { useToast, fehlerNachricht } from "@/lib/toast"

const LEER: PruefungEingabe = {
  pruefungsartId: 0,
  komponenteId: null,
  objektId: null,
  anlageId: null,
  pruefungsdatum: null,
  bestanden: null,
  nochGueltig: true,
  bemerkung: null,
  protokollPaperlessId: null,
}

/** Prüfungsliste mit Ampel-Spalte (AP-3.4, 02-N) + Kontextspalte Anlage/Filter (P2).
 * Anlegen/Bearbeiten geschieht im Sheet von rechts (UX_KONZEPT.md §7) statt auf
 * einer eigenen Route — die Route `/maschinen/pruefungen/:id` bleibt als
 * Deep-Link-Ziel (Suche, externe Links) erhalten und zeigt weiterhin
 * PruefungDetailPage. */
export function PruefungenPage() {
  const [anlageFilter, setAnlageFilter] = useState<string>("")
  const [sheetId, setSheetId] = useState<number | "neu" | null>(null)
  const queryClient = useQueryClient()

  const { data: pruefungen, isLoading, isError, refetch } = useQuery({
    queryKey: ["pruefungen"],
    queryFn: () => fetchPruefungen(),
  })
  const { data: arten } = useQuery({ queryKey: ["pruefungsarten"], queryFn: fetchPruefungsarten })
  const { data: anlagen } = useQuery({ queryKey: ["anlagen"], queryFn: fetchAnlagen })

  const artName = (id: number) => arten?.find((a) => a.id === id)?.pruefungsart ?? `Art #${id}`
  const anlageName = (id: number | null) =>
    id === null ? "—" : anlagen?.find((a) => a.id === id)?.bezeichnung ?? `Anlage #${id}`

  const gefiltert = useMemo(() => {
    if (!anlageFilter) return pruefungen ?? []
    return (pruefungen ?? []).filter((p) => p.anlageId === Number(anlageFilter))
  }, [pruefungen, anlageFilter])

  // Grid-Spalten: header als String, damit die Spalten-Auswahl ihn anzeigen kann
  const spalten = useMemo<ColumnDef<Pruefung>[]>(
    () => [
      {
        id: "pruefungsart",
        header: "Prüfungsart",
        accessorFn: (p) => artName(p.pruefungsartId),
        cell: ({ row }) => (
          <button
            type="button"
            onClick={() => setSheetId(row.original.id)}
            className="rounded-md text-left font-medium text-slate-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
          >
            {artName(row.original.pruefungsartId)}
          </button>
        ),
      },
      {
        id: "anlage",
        header: "Anlage",
        accessorFn: (p) =>
          p.anlageId !== null
            ? anlageName(p.anlageId)
            : p.komponenteId !== null
              ? `Komponente #${p.komponenteId}`
              : "",
        cell: ({ row }) => {
          const p = row.original
          return p.anlageId !== null ? (
            <Link to={`/maschinen/anlagen/${p.anlageId}`} className="hover:underline">
              {anlageName(p.anlageId)}
            </Link>
          ) : p.komponenteId !== null ? (
            <Link to={`/maschinen/komponenten/${p.komponenteId}`} className="hover:underline">
              Komponente #{p.komponenteId}
            </Link>
          ) : (
            "—"
          )
        },
      },
      {
        id: "pruefungsdatum",
        header: "Prüfungsdatum",
        accessorFn: (p) => p.pruefungsdatum ?? "",
        cell: ({ row }) => row.original.pruefungsdatum ?? "—",
      },
      {
        id: "ablaufdatum",
        header: "Ablaufdatum",
        accessorFn: (p) => p.ablaufdatum ?? "",
        cell: ({ row }) => row.original.ablaufdatum ?? "—",
      },
      {
        id: "ergebnis",
        header: "Ergebnis",
        accessorFn: (p) => p.bestanden ?? "",
        cell: ({ row }) => row.original.bestanden ?? "—",
      },
      {
        id: "ampel",
        header: "Ampel",
        accessorFn: (p) => p.ampel,
        cell: ({ row }) => <AmpelBadge ampel={row.original.ampel} />,
      },
    ],
    // artName/anlageName hängen an den Query-Daten
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [arten, anlagen],
  )

  return (
    <div>
      <header className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Prüfungen</h1>
          <p className="mt-1 text-sm text-slate-600">
            Gesetzliche/technische Prüfungen — Ampel aus der Fristen-Engine (§5).
          </p>
        </div>
        <Button type="button" onClick={() => setSheetId("neu")}>
          + Neue Prüfung
        </Button>
      </header>

      <PruefungSheet
        pruefungId={sheetId}
        onOpenChange={(open) => !open && setSheetId(null)}
        onGespeichert={() => queryClient.invalidateQueries({ queryKey: ["pruefungen"] })}
      />

      <div className="mb-4">
        <Select
          value={anlageFilter}
          onChange={(e) => setAnlageFilter(e.target.value)}
          className="w-auto"
        >
          <option value="">Alle Anlagen</option>
          {anlagen?.map((a) => (
            <option key={a.id} value={a.id}>
              {a.bezeichnung}
            </option>
          ))}
        </Select>
      </div>

      {isLoading && (
        <div className="animate-pulse space-y-2" aria-label="Lade Prüfungen …">
          <div className="h-8 rounded-md bg-slate-100" />
          <div className="h-8 rounded-md bg-slate-100" />
          <div className="h-8 rounded-md bg-slate-100" />
        </div>
      )}
      {isError && (
        <p className="text-sm text-red-700">
          Prüfungen konnten nicht geladen werden.{" "}
          <button
            type="button"
            onClick={() => refetch()}
            className="rounded-md underline hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
          >
            Erneut versuchen
          </button>
        </p>
      )}

      {pruefungen && (
        <DataGrid
          columns={spalten}
          data={gefiltert}
          storageKey="pruefungen"
          emptyMessage={
            anlageFilter ? "Keine Prüfungen für diese Anlage." : "Keine Prüfungen erfasst."
          }
        />
      )}
    </div>
  )
}

/** Sheet-Formular für Anlegen/Bearbeiten einer Prüfung (UX_KONZEPT.md §7.3). */
export function PruefungSheet({
  pruefungId,
  onOpenChange,
  onGespeichert,
  vorbelegung,
}: {
  pruefungId: number | "neu" | null
  onOpenChange: (open: boolean) => void
  onGespeichert: () => void
  /** Vorbelegung beim Anlegen — z. B. Komponente aus der Komponentenansicht (Issue #5) */
  vorbelegung?: Partial<PruefungEingabe>
}) {
  const istNeu = pruefungId === "neu"
  const id = istNeu || pruefungId === null ? null : pruefungId
  const toast = useToast()

  const [form, setForm] = useState<PruefungEingabe>(LEER)

  const { data: pruefung } = useQuery({
    queryKey: ["pruefungen", id],
    queryFn: () => fetchPruefung(id!),
    enabled: id !== null,
  })
  const { data: arten } = useQuery({ queryKey: ["pruefungsarten"], queryFn: fetchPruefungsarten })
  const { data: anlagen } = useQuery({ queryKey: ["anlagen"], queryFn: fetchAnlagen })

  // State-Reset beim Öffnen (Muster UX_KONZEPT.md §7.4).
  useEffect(() => {
    if (istNeu) setForm({ ...LEER, ...vorbelegung })
    else if (pruefung) setForm(pruefung)
  }, [istNeu, pruefung, pruefungId, vorbelegung])

  const speichern = useMutation({
    mutationFn: () => (istNeu ? createPruefung(form) : updatePruefung(id!, form)),
    onSuccess: () => {
      toast.success("Prüfung gespeichert.")
      onGespeichert()
      onOpenChange(false)
    },
    onError: (err) => toast.error(fehlerNachricht(err, "Prüfung konnte nicht gespeichert werden.")),
  })

  const loeschen = useMutation({
    mutationFn: () => deletePruefung(id!),
    onSuccess: () => {
      toast.success("Prüfung gelöscht.")
      onGespeichert()
      onOpenChange(false)
    },
    onError: (err) => toast.error(fehlerNachricht(err, "Prüfung konnte nicht gelöscht werden.")),
  })

  const duplizieren = useMutation({
    mutationFn: () => duplizierePruefung(id!),
    onSuccess: () => {
      toast.success("Prüfung dupliziert.")
      onGespeichert()
      onOpenChange(false)
    },
    onError: (err) => toast.error(fehlerNachricht(err, "Prüfung konnte nicht dupliziert werden.")),
  })

  const serviceticket = useMutation({
    mutationFn: () => erstellePruefungServiceticket(id!),
    onSuccess: () => toast.success("Serviceticket erstellt."),
    onError: (err) => toast.error(fehlerNachricht(err, "Serviceticket konnte nicht erstellt werden.")),
  })

  return (
    <Sheet open={pruefungId !== null} onOpenChange={onOpenChange}>
      <SheetContent sheetKey="pruefung-detail" defaultWidth={SHEET_WIDTH.md}>
        <SheetHeader>
          <SheetTitle>{istNeu ? "Neue Prüfung" : `Prüfung #${id}`}</SheetTitle>
          <SheetDescription>
            02-N — Ablaufdatum wird aus Prüfungsdatum + Intervall der Prüfungsart berechnet (§5).
          </SheetDescription>
          {pruefung && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-slate-600">Status:</span>
              <AmpelBadge ampel={pruefung.ampel} />
              <span className="text-slate-500">Ablaufdatum: {pruefung.ablaufdatum ?? "—"}</span>
            </div>
          )}
        </SheetHeader>

        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault()
            speichern.mutate()
          }}
        >
          <div className="space-y-1.5">
            <label className="block text-sm text-slate-700">Prüfungsart</label>
            <Select
              required
              value={form.pruefungsartId || ""}
              onChange={(e) => setForm({ ...form, pruefungsartId: Number(e.target.value) })}
            >
              <option value="">— auswählen —</option>
              {arten?.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.pruefungsart}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1.5">
            <label className="block text-sm text-slate-700">Anlage</label>
            <Select
              value={form.anlageId ?? ""}
              onChange={(e) =>
                setForm({ ...form, anlageId: e.target.value ? Number(e.target.value) : null })
              }
            >
              <option value="">—</option>
              {anlagen?.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.bezeichnung}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1.5">
            <label className="block text-sm text-slate-700">Prüfungsdatum</label>
            <Input
              type="date"
              value={form.pruefungsdatum ?? ""}
              onChange={(e) => setForm({ ...form, pruefungsdatum: e.target.value || null })}
            />
          </div>
          <div className="space-y-1.5">
            <label className="block text-sm text-slate-700">Ergebnis</label>
            <Select
              value={form.bestanden ?? ""}
              onChange={(e) => setForm({ ...form, bestanden: e.target.value || null })}
            >
              <option value="">—</option>
              <option value="bestanden">bestanden</option>
              <option value="mit_maengeln">mit Mängeln</option>
              <option value="nicht_bestanden">nicht bestanden</option>
            </Select>
          </div>
          <div className="space-y-1.5">
            <label className="block text-sm text-slate-700">Bemerkung</label>
            <Textarea
              value={form.bemerkung ?? ""}
              onChange={(e) => setForm({ ...form, bemerkung: e.target.value })}
              rows={3}
            />
          </div>

          {!istNeu && (
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="secondary" onClick={() => duplizieren.mutate()}>
                Prüfung duplizieren
              </Button>
              <Button type="button" variant="secondary" onClick={() => serviceticket.mutate()}>
                Serviceticket erstellen
              </Button>
            </div>
          )}
          {serviceticket.isSuccess && (
            <p className="text-sm text-green-700">Serviceticket erstellt: {serviceticket.data.ticketKey}</p>
          )}

          <SheetFooter>
            {!istNeu && (
              <Button type="button" variant="destructive" onClick={() => loeschen.mutate()}>
                Löschen
              </Button>
            )}
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Abbrechen
            </Button>
            <Button type="submit" variant="primary" disabled={speichern.isPending}>
              Speichern
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  )
}
